<template>
  <div class="lock-screen" :class="{ 'locked': isLocked }" v-if="isLocked">
    <!-- 自定义消息提示容器 -->
    <div class="lock-screen-messages">
      <transition-group name="message-fade">
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="lock-message"
          :class="`lock-message-${msg.type}`">
          <i :class="getMessageIcon(msg.type)"></i>
          <span>{{ msg.text }}</span>
        </div>
      </transition-group>
    </div>

    <!-- 锁定屏幕背景 -->
    <div class="lock-background">
      <div class="blur-overlay"></div>
      <div class="particles" ref="particles"></div>
      <div class="time-display">{{ currentTime }}</div>
    </div>

    <!-- 锁定屏幕内容 -->
    <div class="lock-content">
      <!-- 用户头像和信息 -->
      <div class="user-section">
        <div class="avatar-wrapper">
          <img :src="userInfo.avatar || defaultAvatar" alt="用户头像" class="avatar" />
          <div class="avatar-ring"></div>
        </div>
        <h2 class="user-name">{{ userInfo.nickname || '用户' }}</h2>
      </div>

      <!-- 解锁表单 -->
      <div class="unlock-section">
        <div class="unlock-form">
          <div class="form-group">
            <div class="input-wrapper">
              <i class="el-icon-lock input-icon"></i>
              <input
                type="password"
                v-model="password"
                placeholder="请输入密码解锁"
                @keyup.enter="unlock"
                ref="passwordInput"
                class="password-input"
                :disabled="isLockedByError"
              />
              <i class="el-icon-view eye-icon" @click="togglePasswordVisibility" v-if="password && !isLockedByError"></i>
            </div>
          </div>
          <button class="unlock-btn" @click="unlock" :disabled="!password || unlocking || isLockedByError">
            <i class="el-icon-unlock" v-if="!unlocking"></i>
            <i class="el-icon-loading" v-else></i>
            <span>{{ unlocking ? '解锁中...' : (isLockedByError ? '已锁定' : '解锁屏幕') }}</span>
          </button>
        </div>

        <!-- 错误提示 -->
        <div class="error-message" v-if="errorMessage">
          <i class="el-icon-warning"></i>
          {{ errorMessage }}
        </div>

        <!-- 锁定倒计时 -->
        <div class="lock-countdown" v-if="isLockedByError">
          <i class="el-icon-time"></i>
          <span>{{ lockCountdownMessage }}</span>
        </div>

        <!-- 锁定时间 -->
        <div class="lock-time" v-if="!isLockedByError">
          <i class="el-icon-time"></i>
          <span>已锁定 {{ lockDuration }}</span>
        </div>

        <!-- 忘记密码 -->
        <div class="forgot-password" v-if="!isLockedByError">
          <a @click="showResetPasswordDialog">忘记密码？</a>
        </div>
      </div>
    </div>

    <!-- 重置密码对话框 -->
    <el-dialog
      title="重置锁屏密码"
      :visible.sync="resetPasswordDialogVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      width="400px"
      class="reset-password-dialog">
      <el-form :model="resetPasswordForm" :rules="resetPasswordRules" ref="resetPasswordForm" label-width="80px">
        <el-form-item label="登录密码" prop="loginPassword">
          <el-input
            v-model="resetPasswordForm.loginPassword"
            type="password"
            placeholder="请输入登录密码验证身份"
            show-password>
          </el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="resetPasswordForm.newPassword"
            type="password"
            placeholder="请输入新锁屏密码（4-20位）"
            show-password>
          </el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="resetPasswordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resetPasswordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleResetPassword" :loading="resetting">重置</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { verifyLockScreenPassword, getLockScreenPasswordStatus, resetLockScreenPassword } from '@/api/user'

export default {
  name: 'LockScreen',
  data() {
    return {
      isLocked: false,
      password: '',
      unlocking: false,
      errorMessage: '',
      lockTime: null,
      lockDuration: '0分钟',
      autoLockEnabled: false,
      autoLockMinutes: 30,
      defaultAvatar: 'https://xuanzhuo.oss-cn-beijing.aliyuncs.com/touristAvatar.jpg',
      inactivityTimer: null,
      lastActivityTime: Date.now(),
      currentTime: '',
      timeTimer: null,
      // 密码错误锁定相关
      isLockedByError: false,
      lockUntil: null,
      lockCountdownTimer: null,
      remainingMinutes: 0,
      // 重置密码相关
      resetPasswordDialogVisible: false,
      resetting: false,
      resetPasswordForm: {
        loginPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      resetPasswordRules: {
        loginPassword: [
          { required: true, message: '请输入登录密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 4, max: 20, message: '密码长度必须在4-20位之间', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: this.validateResetConfirmPassword, trigger: 'blur' }
        ]
      },
      // 自定义消息提示
      messages: [],
      messageIdCounter: 0
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.userInfo || {}
    },
    lockCountdownMessage() {
      if (this.remainingMinutes <= 0) {
        return '锁定已过期，请重新尝试'
      }
      return `已锁定，还剩 ${this.remainingMinutes} 分钟后可重试`
    }
  },
  created() {
    this.loadSettings()
    this.setupActivityTracker()
    this.updateTime()
    this.timeTimer = setInterval(this.updateTime, 1000)

    // 检查锁屏状态
    this.checkLockScreenStatus()
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    if (this.lockTime) {
      clearInterval(this.lockTime)
    }
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer)
    }
    if (this.timeTimer) {
      clearInterval(this.timeTimer)
    }
    if (this.lockCountdownTimer) {
      clearInterval(this.lockCountdownTimer)
    }
    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    async lock() {
      try {
        // 先检查是否已设置锁屏密码
        const res = await getLockScreenPasswordStatus()
        if (res.code === 200 && res.data) {
          const hasPassword = res.data.hasPassword
          if (!hasPassword) {
            this.showMessage('请先设置锁屏密码再锁定屏幕', 'warning')
            return false
          }
        }

        this.isLocked = true
        this.password = ''
        this.errorMessage = ''
        this.lockTime = Date.now()

        this.updateLockDuration()
        this.durationTimer = setInterval(this.updateLockDuration, 1000)

        this.$nextTick(() => {
          this.$refs.passwordInput?.focus()
        })

        this.saveLockState(true)
        return true
      } catch (error) {
        console.error('检查锁屏密码状态失败:', error)
        this.showMessage('操作失败，请重试', 'error')
        return false
      }
    },

    async unlock() {
      if (!this.password) return
      this.unlocking = true
      this.errorMessage = ''

      try {
        // 获取用户设备信息
        const unlockDTO = {
          password: this.password,
          ipAddress: await this.getIPAddress(),
          browser: this.getBrowserInfo(),
          os: this.getOSInfo()
        }

        const res = await verifyLockScreenPassword(unlockDTO)

        if (res.code === 200) {
          this.isLocked = false
          this.unlocking = false
          this.password = ''
          this.errorMessage = ''
          this.isLockedByError = false

          if (this.durationTimer) {
            clearInterval(this.durationTimer)
          }

          this.lastActivityTime = Date.now()

          this.saveLockState(false)

          this.showMessage(res.msg || '解锁成功', 'success')
        } else {
          this.errorMessage = res.msg || '解锁失败'
          this.unlocking = false
          this.password = ''

          // 检查是否被锁定
          await this.checkLockScreenStatus()

          if (!this.isLockedByError) {
            this.$refs.passwordInput?.focus()
          }
        }
      } catch (error) {
        this.errorMessage = error.message || '网络错误，请重试'
        this.unlocking = false
        this.password = ''
        this.$refs.passwordInput?.focus()
      }
    },

    async checkLockScreenStatus() {
      try {
        const res = await getLockScreenPasswordStatus()
        if (res.code === 200 && res.data) {
          const data = res.data
          if (data.isLocked && data.lockUntil) {
            this.isLockedByError = true
            this.lockUntil = data.lockUntil
            this.remainingMinutes = data.remainingMinutes
            this.startLockCountdown()
          } else {
            this.isLockedByError = false
            this.lockUntil = null
            this.remainingMinutes = 0
            if (this.lockCountdownTimer) {
              clearInterval(this.lockCountdownTimer)
            }
          }
        }
      } catch (error) {
        console.error('检查锁屏状态失败:', error)
      }
    },

    startLockCountdown() {
      if (this.lockCountdownTimer) {
        clearInterval(this.lockCountdownTimer)
      }

      this.lockCountdownTimer = setInterval(() => {
        if (!this.lockUntil) {
          return
        }

        const now = Date.now()
        const remainingMs = this.lockUntil - now

        if (remainingMs <= 0) {
          this.isLockedByError = false
          this.lockUntil = null
          this.remainingMinutes = 0
          this.errorMessage = ''
          clearInterval(this.lockCountdownTimer)
          this.checkLockScreenStatus()
        } else {
          this.remainingMinutes = Math.ceil(remainingMs / (60 * 1000))
        }
      }, 1000)
    },

    togglePasswordVisibility() {
      const input = this.$refs.passwordInput
      if (input.type === 'password') {
        input.type = 'text'
      } else {
        input.type = 'password'
      }
    },

    updateLockDuration() {
      if (!this.lockTime) return
      const now = Date.now()
      const diff = Math.floor((now - this.lockTime) / 1000)

      if (diff < 60) {
        this.lockDuration = `${diff}秒`
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60)
        this.lockDuration = `${minutes}分钟`
      } else {
        const hours = Math.floor(diff / 3600)
        const minutes = Math.floor((diff % 3600) / 60)
        this.lockDuration = `${hours}小时${minutes}分钟`
      }
    },

    setupActivityTracker() {
      if (!this.autoLockEnabled) return

      const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']

      activityEvents.forEach(event => {
        document.addEventListener(event, this.handleActivity)
      })

      this.inactivityTimer = setInterval(() => {
        const inactiveTime = Date.now() - this.lastActivityTime
        const lockTime = this.autoLockMinutes * 60 * 1000

        if (inactiveTime >= lockTime && !this.isLocked) {
          this.lock()
          this.showMessage('长时间未操作，屏幕已自动锁定', 'warning')
        }
      }, 30000)
    },

    handleActivity() {
      this.lastActivityTime = Date.now()
    },

    updateTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      this.currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    handleKeyDown(event) {
      if (event.ctrlKey && event.key === 'l') {
        event.preventDefault()
        if (!this.isLocked) {
          this.lock()
          this.showMessage('屏幕已锁定', 'success')
        }
      }
    },

    lockScreenSettings() {
      this.showMessage('锁定屏幕功能已启用', 'success')
    },

    saveSettings() {
      const settings = {
        autoLockEnabled: this.autoLockEnabled,
        autoLockMinutes: this.autoLockMinutes
      }
      localStorage.setItem('lockScreenSettings', JSON.stringify(settings))
      this.showMessage('设置已保存', 'success')
    },

    loadSettings() {
      const settings = localStorage.getItem('lockScreenSettings')
      if (settings) {
        const parsed = JSON.parse(settings)
        this.autoLockEnabled = parsed.autoLockEnabled || false
        this.autoLockMinutes = parsed.autoLockMinutes || 30
      }

      const lockState = sessionStorage.getItem('isLocked')
      if (lockState === 'true') {
        this.lock()
      }
    },

    saveLockState(locked) {
      sessionStorage.setItem('isLocked', locked.toString())
    },

    createParticles() {
      const particlesContainer = this.$refs.particles
      if (!particlesContainer) return

      for (let i = 0; i < 50; i++) {
        const particle = document.createElement('div')
        particle.className = 'particle'
        particle.style.left = Math.random() * 100 + '%'
        particle.style.animationDelay = Math.random() * 5 + 's'
        particle.style.animationDuration = (Math.random() * 10 + 10) + 's'
        particlesContainer.appendChild(particle)
      }
    },

    // 获取IP地址
    async getIPAddress() {
      try {
        // 这里可以从localStorage或请求中获取IP
        return localStorage.getItem('user_ip') || '127.0.0.1'
      } catch (error) {
        return '127.0.0.1'
      }
    },

    // 获取浏览器信息
    getBrowserInfo() {
      const ua = navigator.userAgent
      let browser = 'Unknown'
      if (ua.indexOf('Chrome') > -1) {
        browser = 'Chrome'
      } else if (ua.indexOf('Firefox') > -1) {
        browser = 'Firefox'
      } else if (ua.indexOf('Safari') > -1) {
        browser = 'Safari'
      } else if (ua.indexOf('Edge') > -1) {
        browser = 'Edge'
      }
      return browser
    },

    // 获取操作系统信息
    getOSInfo() {
      const ua = navigator.userAgent
      let os = 'Unknown'
      if (ua.indexOf('Windows') > -1) {
        os = 'Windows'
      } else if (ua.indexOf('Mac') > -1) {
        os = 'MacOS'
      } else if (ua.indexOf('Linux') > -1) {
        os = 'Linux'
      } else if (ua.indexOf('Android') > -1) {
        os = 'Android'
      } else if (ua.indexOf('iOS') > -1) {
        os = 'iOS'
      }
      return os
    },

    // 重置密码相关方法
    showResetPasswordDialog() {
      this.resetPasswordDialogVisible = true
      this.resetPasswordForm = {
        loginPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.$nextTick(() => {
        this.$refs.resetPasswordForm?.clearValidate()
      })
    },

    async handleResetPassword() {
      this.$refs.resetPasswordForm.validate(async (valid) => {
        if (valid) {
          this.resetting = true
          try {
            const res = await resetLockScreenPassword({
              loginPassword: this.resetPasswordForm.loginPassword,
              newPassword: this.resetPasswordForm.newPassword
            })

            if (res.code === 200) {
              this.showMessage(res.data || '重置成功', 'success')
              this.resetPasswordDialogVisible = false
              // 清除错误状态
              this.isLockedByError = false
              this.lockUntil = null
              this.remainingMinutes = 0
              this.errorMessage = ''
              if (this.lockCountdownTimer) {
                clearInterval(this.lockCountdownTimer)
              }
              // 自动聚焦到密码输入框
              this.$nextTick(() => {
                this.$refs.passwordInput?.focus()
              })
            } else {
              this.showMessage(res.msg || '重置失败', 'error')
            }
          } catch (error) {
            this.showMessage(error.message || '重置失败', 'error')
          } finally {
            this.resetting = false
          }
        }
      })
    },

    validateResetConfirmPassword(rule, value, callback) {
      if (value !== this.resetPasswordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    },

    // 自定义消息提示方法
    showMessage(text, type = 'info', duration = 3000) {
      const id = ++this.messageIdCounter
      const msg = { id, text, type }
      this.messages.push(msg)

      if (duration > 0) {
        setTimeout(() => {
          const index = this.messages.findIndex(m => m.id === id)
          if (index > -1) {
            this.messages.splice(index, 1)
          }
        }, duration)
      }
    },

    // 获取消息图标
    getMessageIcon(type) {
      const icons = {
        success: 'el-icon-success',
        warning: 'el-icon-warning',
        error: 'el-icon-error',
        info: 'el-icon-info'
      }
      return icons[type] || icons.info
    }
  },
  watch: {
    isLocked(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.createParticles()
        })
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.lock-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;

  &.locked {
    .lock-background {
      opacity: 1;
    }
  }
}

/* 自定义消息提示容器 */
.lock-screen-messages {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

/* 消息提示 */
.lock-message {
  min-width: 300px;
  max-width: 500px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: messageSlideIn 0.3s ease;
  pointer-events: auto;

  i {
    font-size: 18px;
    flex-shrink: 0;
  }

  span {
    flex: 1;
    word-break: break-word;
  }

  &.lock-message-success {
    border-left: 4px solid #67c23a;
    color: #67c23a;

    i {
      color: #67c23a;
    }
  }

  &.lock-message-warning {
    border-left: 4px solid #e6a23c;
    color: #e6a23c;

    i {
      color: #e6a23c;
    }
  }

  &.lock-message-error {
    border-left: 4px solid #f56c6c;
    color: #f56c6c;

    i {
      color: #f56c6c;
    }
  }

  &.lock-message-info {
    border-left: 4px solid #909399;
    color: #909399;

    i {
      color: #909399;
    }
  }
}

/* 消息动画 */
.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter,
.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lock-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0.85;
  transition: opacity 0.3s ease;
}

.blur-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.3);
}

.particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    animation: float linear infinite;
  }
}

.time-display {
  position: absolute;
  top: 2%;
  left: 50%;
  transform: translateX(-50%);
  padding: 20px 50px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 75px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'KaiTi', '楷体', 'STKaiti', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  letter-spacing: 4px;
  pointer-events: none;
  user-select: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

@keyframes float {
  0% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) rotate(720deg);
    opacity: 0;
  }
}

.lock-content {
  position: relative;
  z-index: 10;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 50px 40px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 450px;
  width: 90%;
  animation: slideUp 0.5s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-section {
  text-align: center;
  margin-bottom: 40px;

  .avatar-wrapper {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 20px;

    .avatar {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 4px solid rgba(255, 255, 255, 0.3);
      object-fit: cover;
    }

    .avatar-ring {
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border: 2px solid rgba(255, 255, 255, 0.5);
      border-radius: 50%;
      animation: ringPulse 2s ease-in-out infinite;
    }
  }

  @keyframes ringPulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.5;
    }
  }

  .user-name {
    font-size: 28px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 10px;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }

  .user-email {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
}

.unlock-section {
  margin-bottom: 30px;

  .unlock-form {
    .form-group {
      margin-bottom: 20px;
      max-width: 300px;
      margin-left: auto;
      margin-right: auto;

      .input-wrapper {
        position: relative;

        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.6);
          font-size: 18px;
        }

        .password-input {
          width: 100%;
          padding: 12px 50px 12px 45px;
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: all 0.3s ease;
          box-sizing: border-box;

          &::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          &:focus {
            border-color: rgba(255, 255, 255, 0.6);
            background: rgba(255, 255, 255, 0.2);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }

        .eye-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.6);
          font-size: 18px;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            color: rgba(255, 255, 255, 0.9);
          }
        }
      }
    }

    .unlock-btn {
      width: 100%;
      padding: 12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      max-width: 300px;
      margin: 0 auto;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      i {
        font-size: 16px;
      }
    }
  }

  .error-message {
    margin-top: 15px;
    padding: 10px 15px;
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.5);
    border-radius: 10px;
    color: #fca5a5;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: shake 0.5s ease;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;

    i {
      font-size: 16px;
    }
  }

  @keyframes shake {
    0%, 100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-10px);
    }
    75% {
      transform: translateX(10px);
    }
  }

  .lock-countdown {
    text-align: center;
    margin-top: 20px;
    padding: 10px 15px;
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.5);
    border-radius: 10px;
    color: #fca5a5;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;

    i {
      font-size: 16px;
    }
  }

  .lock-time {
    text-align: center;
    margin-top: 20px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    i {
      font-size: 16px;
    }
  }

  .forgot-password {
    text-align: center;
    margin-top: 15px;

    a {
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        color: rgba(255, 255, 255, 1);
        text-decoration: underline;
      }
    }
  }
}

/* 重置密码对话框样式 */
::v-deep .reset-password-dialog {
  .el-dialog {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 15px;
  }

  .el-dialog__header {
    padding: 20px 20px 10px;

    .el-dialog__title {
      color: #333;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 20px;
  }

  .el-dialog__footer {
    padding: 10px 20px 20px;
  }

  .el-form-item__label {
    color: #333;
  }

  .el-input__inner {
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.1);
    color: #333;

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }

    &:focus {
      border-color: var(--theme-color, #667eea);
      background: rgba(0, 0, 0, 0.08);
    }
  }
}

/* 暗色主题适配 */
html[data-theme='dark'] .reset-password-dialog {
  .el-dialog {
    background: rgba(15, 23, 42, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .el-dialog__title {
    color: #fff;
  }

  .el-form-item__label {
    color: rgba(255, 255, 255, 0.8);
  }

  .el-input__inner {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: #fff;

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    &:focus {
      border-color: rgba(255, 255, 255, 0.3);
      background: rgba(255, 255, 255, 0.08);
    }
  }
}
</style>

/* 暗色主题适配 */
html[data-theme='dark'] .lock-screen {
  .lock-content {
    background: rgba(15, 23, 42, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

/* 亮色主题适配 */
html[data-theme='light'] .lock-screen {
  .lock-background {
    background: linear-gradient(135deg, var(--theme-color) 0%, #667eea 100%);
  }

  .lock-content {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(0, 0, 0, 0.1);

    .user-name {
      color: #333;
    }

    .user-email {
      color: rgba(0, 0, 0, 0.6);
    }

    .unlock-section {
      .unlock-form {
        .form-group {
          .input-wrapper {
            .input-icon {
              color: rgba(0, 0, 0, 0.4);
            }

            .password-input {
              background: rgba(0, 0, 0, 0.05);
              border-color: rgba(0, 0, 0, 0.15);
              color: #333;

              &::placeholder {
                color: rgba(0, 0, 0, 0.4);
              }

              &:focus {
                border-color: var(--theme-color);
                background: rgba(0, 0, 0, 0.08);
              }
            }

            .eye-icon {
              color: rgba(0, 0, 0, 0.4);

              &:hover {
                color: rgba(0, 0, 0, 0.6);
              }
            }
          }
        }
      }

      .lock-time {
        color: rgba(0, 0, 0, 0.5);
      }
    }

    .quick-actions {
      .action-btn {
        background: rgba(0, 0, 0, 0.05);
        border-color: rgba(0, 0, 0, 0.1);
        color: rgba(0, 0, 0, 0.6);

        &:hover {
          background: rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
}
</style>